import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  UploadSimple, Table, ArrowRight, CheckCircle, Warning,
  MapPin, Briefcase, User, Eye
} from "@phosphor-icons/react";

export default function DistributionPage() {
  const { user } = useAuth();
  const [step, setStep] = useState("upload"); // upload | preview | result
  const [dataType, setDataType] = useState("vacancy");
  const [rawText, setRawText] = useState("");
  const [format, setFormat] = useState("csv");
  const [preview, setPreview] = useState(null);
  const [fieldMapping, setFieldMapping] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const isLeader = user?.role === "admin" || user?.role === "team_leader";

  useEffect(() => {
    api.get("/teams/dropdown").then(r => setTeams(r.data || [])).catch(() => {});
  }, []);

  const handleParse = async () => {
    if (!rawText.trim()) { toast.error("Paste or type data first"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/distribution/parse", { type: dataType, raw_text: rawText, format });
      setPreview(data);
      setFieldMapping(data.field_mapping || {});
      setSelectedRows(data.rows?.map((_, i) => i) || []);
      setStep("preview");
    } catch (e) { toast.error(e.response?.data?.detail || "Parse failed"); }
    finally { setLoading(false); }
  };

  const handleImport = async () => {
    if (!selectedRows.length) { toast.error("Select at least one row"); return; }
    setLoading(true);
    try {
      const records = selectedRows.map(i => preview.rows[i]);
      const { data } = await api.post("/distribution/import", {
        type: dataType,
        records,
        field_mapping: fieldMapping,
        team_id: teamId,
      });
      setResult(data);
      setStep("result");
      toast.success(data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setLoading(false); }
  };

  const toggleRow = (idx) => {
    setSelectedRows(prev => prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]);
  };

  const toggleAll = () => {
    if (selectedRows.length === (preview?.rows?.length || 0)) {
      setSelectedRows([]);
    } else {
      setSelectedRows(preview?.rows?.map((_, i) => i) || []);
    }
  };

  if (!isLeader) return (
    <div className="p-8 text-center text-[#6B7280]">Only Team Leaders and Admin can distribute data.</div>
  );

  return (
    <div data-testid="distribution-page" className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-[#0F172A]">Vacancy & Candidate Distribution</h1>
        <p className="text-sm text-[#6B7280]">Upload, parse and distribute vacancies or candidates to your team</p>
      </div>

      {/* Step: Upload */}
      {step === "upload" && (
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-6 space-y-4" data-testid="upload-step">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-[#6B7280] mb-1 block">Data Type</label>
              <select className="w-full border border-[#D1D5DB] rounded-lg px-3 py-2 text-sm" value={dataType} onChange={e => setDataType(e.target.value)} data-testid="data-type-select">
                <option value="vacancy">Vacancies</option>
                <option value="candidate">Candidates</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-[#6B7280] mb-1 block">Format</label>
              <select className="w-full border border-[#D1D5DB] rounded-lg px-3 py-2 text-sm" value={format} onChange={e => setFormat(e.target.value)} data-testid="format-select">
                <option value="csv">CSV (comma-separated)</option>
                <option value="json">JSON</option>
                <option value="lines">Simple lines</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-[#6B7280] mb-1 block">Assign to Team</label>
              <select className="w-full border border-[#D1D5DB] rounded-lg px-3 py-2 text-sm" value={teamId} onChange={e => setTeamId(e.target.value)} data-testid="team-select">
                <option value="">Select team...</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-[#6B7280] mb-1 block">Paste Data</label>
            <textarea
              className="w-full border border-[#D1D5DB] rounded-lg px-3 py-2 text-sm min-h-[200px] font-mono focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
              placeholder={dataType === "vacancy"
                ? "job_title,employer,location,salary\nNurse Manager,ABC Care,London,£35000\nSocial Worker,City Council,Manchester,£32000"
                : "name,email,phone,location\nJohn Smith,john@example.com,07700123456,London"}
              value={rawText}
              onChange={e => setRawText(e.target.value)}
              data-testid="raw-data-input"
            />
          </div>

          <Button onClick={handleParse} disabled={loading} className="bg-[#C5A059] text-[#0F172A] gap-1.5" data-testid="parse-btn">
            <Eye size={14} /> Parse & Preview
          </Button>
        </div>
      )}

      {/* Step: Preview */}
      {step === "preview" && preview && (
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-6 space-y-4" data-testid="preview-step">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-bold text-[#0F172A]">Preview — {preview.total_rows} {dataType === "vacancy" ? "vacancies" : "candidates"} found</h2>
              {preview.unmapped_fields?.length > 0 && (
                <p className="text-xs text-[#D97706] flex items-center gap-1 mt-1">
                  <Warning size={12} /> Unmapped fields: {preview.unmapped_fields.join(", ")}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setStep("upload")}>Back</Button>
              <Button size="sm" className="bg-[#0F172A] gap-1" onClick={handleImport} disabled={loading} data-testid="import-btn">
                <UploadSimple size={14} /> Import {selectedRows.length} Selected
              </Button>
            </div>
          </div>

          {/* Field mapping */}
          <div className="border border-[#E5E7EB] rounded-lg p-3 bg-[#F9FAFB]">
            <p className="text-xs font-semibold text-[#6B7280] mb-2 uppercase">Field Mapping</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(fieldMapping).map(([src, dst]) => (
                <span key={src} className="text-[10px] bg-white border border-[#E5E7EB] rounded px-2 py-1">
                  <span className="text-[#6B7280]">{src}</span> <ArrowRight size={8} className="inline" /> <span className="font-semibold text-[#0F172A]">{dst}</span>
                </span>
              ))}
            </div>
          </div>

          {/* Data table */}
          <div className="overflow-x-auto border border-[#E5E7EB] rounded-lg">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-[#F1F5F9]">
                  <th className="px-2 py-2 text-left">
                    <input type="checkbox" checked={selectedRows.length === preview.rows.length} onChange={toggleAll} />
                  </th>
                  {preview.headers?.map(h => (
                    <th key={h} className="px-2 py-2 text-left font-semibold text-[#374151]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.rows?.slice(0, 50).map((row, i) => (
                  <tr key={i} className={`border-t border-[#F3F4F6] ${selectedRows.includes(i) ? "bg-[#FFFBEB]" : "hover:bg-[#F9FAFB]"}`} data-testid={`preview-row-${i}`}>
                    <td className="px-2 py-1.5">
                      <input type="checkbox" checked={selectedRows.includes(i)} onChange={() => toggleRow(i)} />
                    </td>
                    {preview.headers?.map(h => (
                      <td key={h} className="px-2 py-1.5 text-[#374151] truncate max-w-[200px]">{row[h] || ""}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Step: Result */}
      {step === "result" && result && (
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-6 text-center" data-testid="result-step">
          <CheckCircle size={48} className="text-green-600 mx-auto mb-3" weight="fill" />
          <h2 className="text-lg font-bold text-[#0F172A] mb-2">{result.message}</h2>
          <p className="text-sm text-[#6B7280]">Imported {result.imported} of {result.total} records</p>
          {result.errors?.length > 0 && (
            <div className="mt-3 bg-red-50 rounded-lg p-3 text-left">
              <p className="text-xs font-semibold text-red-700 mb-1">Errors:</p>
              {result.errors.map((e, i) => <p key={i} className="text-xs text-red-600">{e}</p>)}
            </div>
          )}
          <Button onClick={() => { setStep("upload"); setRawText(""); setPreview(null); setResult(null); }} className="mt-4 bg-[#0F172A]" data-testid="new-import-btn">
            New Import
          </Button>
        </div>
      )}
    </div>
  );
}
